import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useToggle } from "react-use";
import { PF_ROLE_LABELS, PF_ROLES, type PfRole, type Userdata } from "@/types/user";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fileToBase64 } from "./helpers";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { FormInput } from "@/components/forms/input";
import { useCreateUserMutation, useUpdateUserMutation } from "@/api/users-admin";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { toast } from "react-toastify";
import { AdminEmailChangeDialog } from "./AdminEmailChangeDialog";

interface Props {
  user?: Userdata;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

type RoleChecks = Record<PfRole, boolean>;

interface FormInputs {
  password: string;
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  avatar: FileList;
  is_active: boolean;
  roles: RoleChecks;
}

const canActorGrantRole = (actorRoles: PfRole[], role: PfRole): boolean => {
  if (actorRoles.includes("superadmin")) return true;
  if (actorRoles.includes("organization_admin")) return role !== "superadmin";
  return false;
};

const buildDefaultRoles = (user: Userdata | undefined): RoleChecks =>
  PF_ROLES.reduce((acc, role) => {
    acc[role] = user ? user.pfRoles.includes(role) : false;
    return acc;
  }, {} as RoleChecks);

export const UserModal: React.FC<Props> = ({ user, isOpen, setIsOpen }) => {
  const authenticatedUser = useAuthenticatedUser();
  const grantableRoles = React.useMemo(
    () => PF_ROLES.filter(role => canActorGrantRole(authenticatedUser.pfRoles, role)),
    [authenticatedUser.pfRoles],
  );

  const methods = useForm<FormInputs>({
    defaultValues: {
      password: "",
      name: user?.name || "",
      surname: user?.surname || "",
      email: user?.email || "",
      phone_number: user?.phoneNumber || "",
      is_active: user ? user.isActive : true,
      roles: buildDefaultRoles(user),
    },
  });

  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [isEmailChangeOpen, toggleEmailChangeOpen] = useToggle(false);
  const emailChangeButtonRef = React.useRef<HTMLButtonElement>(null);

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    const avatarFile = payload.avatar.item(0);
    const avatar = avatarFile ? (await fileToBase64(avatarFile)).replace(/^data:(.*,)?/, "") : "";

    const rolesFromForm = grantableRoles.filter(role => payload.roles[role]);
    const preservedRoles = user ? user.pfRoles.filter(role => !grantableRoles.includes(role)) : [];
    const pf_roles = Array.from(new Set<PfRole>([...rolesFromForm, ...preservedRoles]));
    const sharedPayload = {
      password: payload.password,
      name: payload.name,
      surname: payload.surname,
      phone_number: payload.phone_number || undefined,
      avatar,
      is_active: payload.is_active,
      pf_roles,
    };

    try {
      if (user) {
        await updateUser({ ...sharedPayload, id: user.id }).unwrap();
      } else {
        await createUser({
          ...sharedPayload,
          username: payload.email,
          email: payload.email,
        }).unwrap();
      }

      setIsOpen(false);
      toast.success(user ? "User change" : "User added");
    } catch (error) {
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  const handleGenerateRandomPassword = () => {
    methods.setValue("password", Math.random().toString(36).slice(-12));
  };

  return (
    <>
      <FormProvider {...methods}>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent>
            <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
              <DialogHeader>
                <DialogTitle>{user ? "Update user" : "Create user"}</DialogTitle>
              </DialogHeader>

              <FormInput name="name" placeholder="Enter name" className="my-3" />
              <FormInput name="surname" placeholder="Enter surname" className="my-3" />
              {user ? (
                <div className="my-3 space-y-2">
                  <Label htmlFor="admin-user-current-email">Email</Label>
                  <Input id="admin-user-current-email" type="email" value={user.email} readOnly />
                  {user.isActive ? (
                    <Button
                      ref={emailChangeButtonRef}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => toggleEmailChangeOpen(true)}
                    >
                      Change email address
                    </Button>
                  ) : null}
                </div>
              ) : (
                <FormInput name="email" placeholder="Enter email" className="my-3" />
              )}
              <FormInput name="phone_number" placeholder="Enter phone number" className="my-3" />
              <FormInput name="password" placeholder="Password (leave empty to not change)" className="mb-1 mt-3" />
              <div
                className="mb-4 cursor-pointer text-right text-xs text-primary"
                onClick={handleGenerateRandomPassword}
              >
                generate random password
              </div>
              <FormInput name="avatar" type="file" className="mb-1 mt-3" accept="image/png, image/jpeg" />
              <div className="items-cent er my-3 flex">
                <input type="checkbox" {...methods.register("is_active")} id="is_active" className="mr-2" />
                <label htmlFor="is_active" className="cursor-pointer text-sm">
                  Is active?
                </label>
              </div>
              {grantableRoles.length > 0 && (
                <div className="my-4">
                  <div className="mb-2 text-sm font-medium text-gray-700">Roles</div>
                  <div className="flex flex-col gap-2">
                    {grantableRoles.map(role => (
                      <div key={role} className="flex items-center">
                        <input
                          type="checkbox"
                          {...methods.register(`roles.${role}`)}
                          id={`role-${role}`}
                          className="mr-2"
                        />
                        <label htmlFor={`role-${role}`} className="cursor-pointer text-sm">
                          {PF_ROLE_LABELS[role]}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={methods.formState.isSubmitting}>
                  {methods.formState.isSubmitting ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </FormProvider>
      {user ? (
        <AdminEmailChangeDialog
          user={user}
          open={isEmailChangeOpen}
          onOpenChange={toggleEmailChangeOpen}
          returnFocusRef={emailChangeButtonRef}
        />
      ) : null}
    </>
  );
};
